import React, { useCallback, useMemo } from "react";
import ReactDOMServer from "react-dom/server";
import { Marker as LeafletMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "@mui/material";
import SvgMarker from "../styled/SvgMarker";
import { isLocationCapabilityRankEligible } from "./capabilityVerification";
import {
  buildMapMarkerTitle,
  getAccessRestrictionLabel,
  isMapMarkerVisible,
} from "./mapEdDisplay";
import { getFacilityTypeLabel, isEmergencyDepartment, isUrgentCare } from "../../constants/facilityTypes";
import { passesWaitTimeFilter } from "./waitTimeFilter";

// CMS OP-18b normalization range for ED pin hue.
// Based on observed live data range (hlthdsk-experimental, June 2026).
// Update if CMS data distribution shifts significantly.
// Lower score_op18b = shorter wait = better = greener pin (hue → 1.0).
// Higher score_op18b = longer wait = worse = redder pin (hue → 0.0).
const ED_WAIT_HUE_MIN_MINUTES = 40; // ~best observed: ~38–42 min → hue 1.0 (green)
const ED_WAIT_HUE_MAX_MINUTES = 464; // ~worst observed: 464 min → hue 0.0 (red)

// SvgMarker renders an 869×1454 SVG at scale(0.04) → ~35×58 px on screen
const BASE_ICON_SIZE = [36, 58];
const BASE_ICON_ANCHOR = [18, 58];
const HIGHLIGHT_ICON_SIZE = [45, 73]; // 1.25× base
const HIGHLIGHT_ICON_ANCHOR = [22, 73];

const Marker = ({
  data,
  filter,
  setModalOpen,
  setModalVisible,
  highlightFacilityIds = [],
  matchDataById = {},
  includeUnverifiedFacilities = false,
  forceHideRestrictedAccess = false,
  hideLongWaitEds = false,
}) => {
  const theme = useTheme();
  const {
    title,
    queueEnabled,
    lat,
    lng,
    rating,
    status,
    customPhone,
    hlthdsk_score,
    waitTimes,
    activeWaitTime,
  } = data;

  // Returns 0.0–1.0 for the pin gradient (0=red/worst, 1=green/best).
  // ED: inverted score_op18b (OP-18b median visit minutes; lower=greener).
  // UC/Clinic: hlthdsk_score.total (0–100).
  // Null/missing data → 0.5 neutral for EDs, 0 for UC/Clinic.
  const defaultBaseHue = useMemo(() => {
    if (isEmergencyDepartment(data)) {
      const op18b = data.score_op18b;
      if (op18b != null) {
        const normalized =
          (Number(op18b) - ED_WAIT_HUE_MIN_MINUTES) /
          (ED_WAIT_HUE_MAX_MINUTES - ED_WAIT_HUE_MIN_MINUTES);
        const clamped = Math.max(0, Math.min(1, normalized));
        return 1 - clamped;
      }
      return 0.5;
    }
    if (hlthdsk_score?.total !== undefined) {
      return hlthdsk_score.total / 100;
    }
    return 0;
  }, [data, hlthdsk_score]);

  const locationId = data.id || data.locationId;
  const matchDataEntry = matchDataById?.[locationId];

  const baseHue = useMemo(() => {
    if (
      matchDataEntry &&
      matchDataEntry.matchTierHue != null &&
      matchDataEntry.matchTierHue !== undefined
    ) {
      return matchDataEntry.matchTierHue;
    }
    return defaultBaseHue;
  }, [matchDataEntry, defaultBaseHue]);

  // starCount: integer 0–5 for the star badge. 0 = no badge shown.
  const starCount = useMemo(() => {
    if (isEmergencyDepartment(data)) {
      // CMS star rating: 1–5. null/undefined/0 → no badge.
      if (rating != null && rating >= 1) return Math.round(rating);
      return 0;
    }
    // UC / Clinic: derive from hlthdsk_score.total (0–100) → 0–5 stars
    const total = hlthdsk_score?.total;
    if (total == null) return 0;
    if (total >= 80) return 5;
    if (total >= 60) return 4;
    if (total >= 40) return 3;
    if (total >= 20) return 2;
    if (total >= 1) return 1;
    return 0;
  }, [data, rating, hlthdsk_score]);

  const isVisible = useMemo(
    () => isMapMarkerVisible(data, filter, { forceHideRestrictedAccess }),
    [
      data,
      filter,
      forceHideRestrictedAccess,
    ]
  );

  const markerHoverTitle = useMemo(() => buildMapMarkerTitle(data), [data]);

  // /scmap capability match: is this facility one of the highlighted (matched) facilities?
  const isHighlighted =
    Array.isArray(highlightFacilityIds) && highlightFacilityIds.includes(locationId);

  // Tooltip text. N (matchScore) is provided via matchDataById.
  const mapScoreValue =
    matchDataEntry && typeof matchDataEntry === "object"
      ? matchDataEntry.matchScore
      : null;
  const mapRequiredValue =
    matchDataEntry && typeof matchDataEntry === "object"
      ? matchDataEntry.requiredCapabilitiesCount
      : null;

  const matchCount = Number.isFinite(mapScoreValue)
    ? mapScoreValue
    : Number.isFinite(data.matchScore)
    ? data.matchScore
    : null;
  const requiredCount = Number.isFinite(mapRequiredValue)
    ? mapRequiredValue
    : Number.isFinite(data.requiredCapabilitiesCount)
    ? data.requiredCapabilitiesCount
    : null;
  const restrictionLabel = getAccessRestrictionLabel(data.accessRestriction);
  const facilityTypeLabel = getFacilityTypeLabel(data);
  const waitMinutesSegment =
    isEmergencyDepartment(data) && data.score_op18b != null
      ? ` · Avg visit: ${Math.round(Number(data.score_op18b))} min`
      : "";
  const matchTooltip = isHighlighted
    ? matchDataEntry
      ? `${facilityTypeLabel} · ${title}${data.pediatricOnly ? " · Children's hospital" : ""}${restrictionLabel ? ` · ${restrictionLabel}` : ""} · ${matchCount}/${requiredCount} tests available${waitMinutesSegment}`
      : matchCount != null
      ? requiredCount != null
        ? `${title} has ${matchCount}/${requiredCount} of the tests you may need`
        : `${title} has ${matchCount} of the tests you may need`
      : `${title} matches your health needs`
    : null;

  const highlightColor = theme.palette.secondary.main;

  const facilityKind = useMemo(() => {
    if (isEmergencyDepartment(data)) return "ed";
    if (isUrgentCare(data)) return "urgent_care";
    return "clinic";
  }, [data]);

  // Memoize icon creation
  const icon = useMemo(() => {
    const markerHtml = ReactDOMServer.renderToString(
      <SvgMarker
        queueEnabled={queueEnabled}
        baseHue={baseHue}
        visible={isVisible}
        facilityKind={facilityKind}
        starCount={starCount}
      />
    );
    // Highlighted (matched) facilities: slight scale increase + secondary-colored glow.
    const hasMatchData = Boolean(matchDataEntry);
    const highlightRingStyle = hasMatchData
      ? " box-shadow: 0 0 0 3px #ffffff;"
      : "";
    const html = isHighlighted
      ? `<div style="transform: scale(1.25); transform-origin: bottom center; filter: drop-shadow(0 0 6px ${highlightColor}) drop-shadow(0 0 2px ${highlightColor});${highlightRingStyle}">${markerHtml}</div>`
      : markerHtml;
    return new L.divIcon({
      html,
      className: "",
      iconSize: isHighlighted ? HIGHLIGHT_ICON_SIZE : BASE_ICON_SIZE,
      iconAnchor: isHighlighted ? HIGHLIGHT_ICON_ANCHOR : BASE_ICON_ANCHOR,
    });
  }, [
    queueEnabled,
    baseHue,
    isVisible,
    facilityKind,
    starCount,
    isHighlighted,
    highlightColor,
    matchDataEntry,
  ]);

  // Memoize click handler
  const handleClick = useCallback(() => {
    setModalVisible(true);
    setModalOpen({
      ...data,
      phone: customPhone,
      location: { latitude: lat, longitude: lng },
      // Ensure waitTimes and activeWaitTime are passed to the modal properly
      waitTimes: waitTimes ? [...waitTimes] : undefined,
      activeWaitTime: activeWaitTime,
    });
    // Log for debugging
  }, [
    data,
    customPhone,
    lat,
    lng,
    waitTimes,
    activeWaitTime,
    setModalOpen,
    setModalVisible,
  ]);

  // Only show approved facilities (or if status is not defined, assume approved)
  if (status && status !== "approved") return null;
  if (!includeUnverifiedFacilities && !isLocationCapabilityRankEligible(data)) {
    return null;
  }
  if (hideLongWaitEds && !passesWaitTimeFilter(data, true)) {
    return null;
  }

  return isVisible ? (
    <LeafletMarker
      position={[lat, lng]}
      title={markerHoverTitle}
      eventHandlers={{ click: handleClick }}
      icon={icon}
    >
      {matchTooltip ? <Tooltip direction="top">{matchTooltip}</Tooltip> : null}
    </LeafletMarker>
  ) : null;
};

export default React.memo(Marker);
